import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useMemo } from 'react'

import EmptyState from '@/components/EmptyState'
import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { getNews } from '@/services/news'
import { getDetailSezRegions, getDetailVideoList } from '@/services/sez-regions'
import { NewsListRequest } from '@/types/news'
import { BaseRequest, ListBasicRequest } from '@/types/sez-regions'
import { formatEpoch } from '@/utils/formatter'
import { useAppSelector } from '@/utils/hooks'
import { titleSez } from '@/utils/string'

const Media: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const requestList: ListBasicRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug), skip: 0, limit: 1000 },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const requestNewsList: NewsListRequest = useMemo(() => {
    return {
      params: { skip: 0, limit: 1000, keyword: { slugSez: String(router.query?.slug) } },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const request: BaseRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug) },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data: dataSez } = useAppSelector(getDetailSezRegions.select(request))
  const { data } = useAppSelector(getDetailVideoList.select(requestList))
  const { data: newsList } = useAppSelector(getNews.select(requestNewsList))

  const mainVideo = useMemo(() => {
    return data?.data.items.filter((item) => item.isProfileVideo)
  }, [data?.data.items])

  const otherVideo = useMemo(() => {
    return data?.data.items.filter((item) => !item.isProfileVideo)
  }, [data?.data.items])

  if (Number(mainVideo?.length) > 0 || Number(otherVideo?.length) > 0 || Number(newsList?.data.items.length) > 0) {
    return (
      <div className='container flex w-full flex-col gap-6 lg:gap-8'>
        {mainVideo && mainVideo.length > 0 ? (
          <div className='flex w-full flex-col gap-4 lg:gap-8'>
            <h5 className='text-lg font-bold leading-7 text-gray-1 lg:text-2xl lg:leading-8'>
              {titleSez(String(dataSez?.data.title), String(router.locale), true)}
            </h5>
            <div className='w-full'>
              <iframe
                src={`https://www.youtube.com/embed/${mainVideo[0].url}`}
                className='h-[193px] w-full rounded-lg md:h-[468px]'
              />
            </div>
          </div>
        ) : null}

        {newsList && newsList.data.items && newsList.data.items.length > 0 && (
          <div className='flex w-full flex-col gap-4 lg:gap-8'>
            <h5 className='text-lg font-bold leading-7 text-gray-1 lg:text-2xl lg:leading-8'>{t('common:news')}</h5>

            <div className='grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-7.5'>
              {newsList.data.items.map((item) => (
                <div
                  key={item.xid}
                  className='lg:border-b-1 group relative overflow-hidden border-b before:absolute before:bottom-0 before:left-0 before:z-10 before:h-1 before:w-0 before:bg-orange before:transition-all before:duration-300 lg:rounded-xl lg:border lg:border-form-border lg:hover:border-transparent lg:hover:shadow-card lg:hover:before:w-full'
                >
                  <div className='flex flex-row-reverse gap-4 lg:flex-col lg:gap-3.5'>
                    <div className='h-15 w-[106px] shrink-0 basis-[106px] overflow-hidden lg:h-[205px] lg:w-full lg:flex-1'>
                      <ImageByWidthDevice
                        images={item.imageFiles}
                        alt={item.title}
                        className='h-full w-full rounded object-cover object-center transition-all duration-300 lg:h-[205px] lg:group-hover:scale-125'
                      />
                    </div>

                    <h4 className='line-clamp-3 flex-1 text-xs font-bold leading-normal text-dark lg:line-clamp-2 lg:px-4 lg:text-sm lg:font-semibold lg:tracking-[-0.084px]'>
                      {item.title}
                    </h4>
                  </div>

                  <div className='flex w-full flex-col gap-2 bg-white pb-4 pt-2 lg:gap-3.5 lg:px-4'>
                    <div
                      className='line-clamp-2 break-all text-xs leading-normal text-gray-2'
                      dangerouslySetInnerHTML={{ __html: item.shortDescription }}
                    />

                    <div className='flex items-center justify-between lg:border-t lg:border-t-accent-light/10 lg:pt-3'>
                      <p className='text-xs leading-normal text-secondary'>
                        {formatEpoch(item.createdAt, 'EEEE, dd LLLL yyyy | T', router.locale)}
                      </p>

                      <Link href={`/media/press/${item.slug}`}>
                        <span className='kek-arrow-right hidden cursor-pointer text-[24px] lg:block' />

                        <span className='text-xs font-semibold leading-normal text-red lg:hidden'>
                          {t('common:readMore')}
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {otherVideo && otherVideo.length > 0 ? (
          <div className='flex w-full flex-col gap-4 lg:gap-8'>
            <h5 className='text-lg font-bold leading-7 text-gray-1 lg:text-2xl lg:leading-8'>
              {`${router.locale === 'id' ? t('common:relatedVideo') : ''} ${dataSez?.data.title} 
            ${router.locale === 'en' ? t('common:relatedVideo') : ''}`}
            </h5>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:justify-center lg:grid-cols-3'>
              <>
                {otherVideo.map((video) => (
                  <div
                    key={video.url}
                    className='flex w-full flex-col gap-3'
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${video.url}`}
                      className='h-[193px] w-full rounded-lg md:h-[205px]'
                    />

                    <div className='flex flex-col gap-2'>
                      <h4 className='line-clamp-2 break-all text-sm font-semibold leading-normal tracking-[-0.6%] text-dark'>
                        {video.title}
                      </h4>

                      <p className='text-xs leading-normal text-gray-2'>
                        {formatEpoch(video.createdAt, 'EEEE, dd LLLL yyyy | T', router.locale)}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <EmptyState
      title={String(t('common:emptyState.title'))}
      description={String(t('common:emptyState.description'))}
      contentWidth='lg:w-full'
    />
  )
}

export default Media
